import type { DirEntry } from "@racler/protocol";

/** Project-relative POSIX paths. "" is the Project root. The agent re-validates everything; this is UX only. */
export const joinPath = (dir: string, name: string) => (dir ? `${dir}/${name}` : name);
export const parentOf = (p: string) => (p.includes("/") ? p.slice(0, p.lastIndexOf("/")) : "");
export const baseName = (p: string) => p.slice(p.lastIndexOf("/") + 1);

/** True if `path` is `base` or lives below it. */
export const isInside = (path: string, base: string) => path === base || path.startsWith(`${base}/`);

/** Where a path ends up after `from` was renamed/moved to `to` (unchanged when it is not affected). */
export function remapPath(path: string, from: string, to: string): string {
  return isInside(path, from) ? to + path.slice(from.length) : path;
}

export type MoveCheck = { ok: true; to: string } | { ok: false; reason: string; silent?: boolean };

/**
 * Can `source` be dropped onto the directory `destDir` ("" = Project root)? Mirrors the agent's rules so the UI
 * can refuse early; the agent still enforces them (Project boundary, collisions, reserved names).
 */
export function checkMove(source: { path: string; kind: DirEntry["kind"] }, destDir: string): MoveCheck {
  if (source.path === "") return { ok: false, reason: "The Project root cannot be moved." };
  if (parentOf(source.path) === destDir)
    return { ok: false, reason: "Already in this folder.", silent: true };
  if (source.kind === "directory" && isInside(destDir, source.path)) {
    return { ok: false, reason: "A folder cannot be moved into itself or one of its subfolders." };
  }
  return { ok: true, to: joinPath(destDir, baseName(source.path)) };
}
